import { useContext, useEffect, useState } from "react";
import Utility from "../utils/utility";
import { collection, query, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";
import { callFunction } from "../api/callFunction";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
  PlayCircleIcon,
  LinkIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  UserGroupIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { GlobalContext } from "../context/GlobalContext";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";

const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    badge:
      "bg-amber-100 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20",
    dot: "bg-amber-500",
  },
  "under-review": {
    label: "Under review",
    badge:
      "bg-blue-100 text-blue-800 ring-blue-600/20 dark:bg-blue-400/10 dark:text-blue-300 dark:ring-blue-400/20",
    dot: "bg-blue-500",
  },
  approved: {
    label: "Approved",
    badge:
      "bg-emerald-100 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20",
    dot: "bg-emerald-500",
  },
  rejected: {
    label: "Rejected",
    badge:
      "bg-rose-100 text-rose-700 ring-rose-600/20 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/20",
    dot: "bg-rose-500",
  },
};

const FILTERS = ["all", "pending", "approved", "rejected"];
const FILTER_LABELS = { all: "All", pending: "Pending", approved: "Approved", rejected: "Rejected" };

const ANSWER_LABELS = {
  whyHost: "Why do you want to host?",
  experience: "Experience / community",
  availability: "Availability",
  socialLink: "Social link",
};

function toDate(dateValue) {
  return Utility.convertDate(dateValue);
}

function formatDate(dateValue) {
  const date = toDate(dateValue);
  if (!date) return "N/A";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Compact "how long ago" for the cards; full timestamp stays in the modal.
function timeAgo(dateValue) {
  const date = toDate(dateValue);
  if (!date) return "";
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function HostApplications() {
  const { globalState } = useContext(GlobalContext);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Live profile photos, resolved by uid, for applications whose doc has no
  // snapshotted photoURL (older ones created before the server started storing it).
  const [photoByUid, setPhotoByUid] = useState({});

  useEffect(() => {
    const q = query(collection(db, "hostApplications"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setApplications(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("hostApplications listen failed:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Keep the open modal in sync with live snapshot updates (status flips, etc).
  useEffect(() => {
    if (!viewing) return;
    const fresh = applications.find((a) => a.id === viewing.id);
    if (fresh && fresh !== viewing) setViewing(fresh);
  }, [applications, viewing]);

  // Resolve a live profile photo for any application missing a snapshotted
  // photoURL, so the queue always shows a face. Fetched once per uid and cached
  // (the `in` guard also caches misses, so it never re-fetches or loops).
  useEffect(() => {
    const missing = [
      ...new Set(
        applications
          .filter((a) => !a.photoURL && a.uid && !(a.uid in photoByUid))
          .map((a) => a.uid)
      ),
    ];
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, "users", uid));
            return [uid, (snap.exists() && snap.data().photoURL) || ""];
          } catch (_) {
            return [uid, ""];
          }
        })
      );
      if (!cancelled) setPhotoByUid((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => {
      cancelled = true;
    };
  }, [applications, photoByUid]);

  const review = async (application, decision, reviewNotes) => {
    if (
      decision === "approved" &&
      !window.confirm(
        "Approve this applicant? This provisions their host profile and texts them to finish setup in the panel."
      )
    ) {
      return;
    }
    setUpdatingId(application.id);
    try {
      await callFunction("adminReviewHostApplication", { applicationId: application.id, decision, reviewNotes: reviewNotes || "" });
      if (decision === "approved") {
        toast.success("Approved — host provisioned and texted to finish setup in the panel.");
      } else if (decision === "reopen") {
        toast.success("Application reopened — back in the pending queue.");
        setViewing(null);
      } else {
        toast.success("Application rejected.");
      }
    } catch (err) {
      console.error("adminReviewHostApplication failed:", err);
      toast.error(err?.message || "Couldn't update the application.");
    } finally {
      setUpdatingId(null);
    }
  };

  const remove = async (application) => {
    if (
      !window.confirm(
        `Delete ${application.name || "this applicant"}'s application? This permanently removes the application and its intro video, and can't be undone.`
      )
    ) {
      return;
    }
    setDeletingId(application.id);
    try {
      await callFunction("adminDeleteHostApplication", { applicationId: application.id });
      toast.success("Application deleted.");
      setViewing(null);
    } catch (err) {
      console.error("adminDeleteHostApplication failed:", err);
      toast.error(err?.message || "Couldn't delete the application.");
    } finally {
      setDeletingId(null);
    }
  };

  const counts = { all: applications.length };
  for (const a of applications) counts[a.status] = (counts[a.status] || 0) + 1;

  const filtered = applications.filter((a) => {
    const matchesStatus = statusFilter === "all" || a.status === statusFilter;
    const haystack = `${a.name || ""} ${a.city || ""} ${a.answers?.whyHost || ""}`.toLowerCase();
    return matchesStatus && haystack.includes(searchTerm.toLowerCase());
  });

  const wrapperClass = "min-h-screen bg-gray-100 dark:bg-gray-900 p-4 sm:p-6 lg:p-8";
  const gridClass =
    "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5";

  if (loading) {
    return (
      <div className={wrapperClass}>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Host Applications
        </h1>
        <div className={gridClass}>
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-white/10"
            >
              <div className="aspect-[4/5] animate-pulse bg-gray-200 dark:bg-gray-700" />
              <div className="space-y-2 p-4">
                <div className="h-3 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
          Host Applications
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Intro videos and applications from the become-a-host page.
        </p>
      </div>

      {/* Search + status filters */}
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-xs">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, city, or pitch…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-lg border-gray-300 py-2.5 pl-10 pr-3 text-sm shadow-sm focus:border-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 lg:pb-0">
          {FILTERS.map((status) => {
            const active = statusFilter === status;
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`inline-flex flex-shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary text-white shadow-sm"
                    : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-gray-700"
                }`}
              >
                {FILTER_LABELS[status]}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                    active
                      ? "bg-white/25 text-white"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                  }`}
                >
                  {counts[status] || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 py-16 text-center dark:border-gray-700">
          <UserGroupIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== "all"
              ? "No applications match your filters."
              : "No host applications yet."}
          </p>
        </div>
      ) : (
        <div className={gridClass}>
          {filtered.map((app) => {
            const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
            const photo = app.photoURL || photoByUid[app.uid];
            return (
              <button
                key={app.id}
                onClick={() => setViewing(app)}
                className="group flex w-full flex-col overflow-hidden rounded-2xl bg-white text-left shadow-sm ring-1 ring-gray-200 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:ring-white/10"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-gray-100 dark:bg-gray-900">
                  {photo ? (
                    <img
                      src={variantURL(photo, 512)}
                      onError={fallbackToOriginal(photo)}
                      alt={app.name || "Applicant"}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-900">
                      <UserCircleIcon className="h-24 w-24 text-gray-300 dark:text-gray-600" />
                    </div>
                  )}

                  <span
                    className={`absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 backdrop-blur-sm ${statusConfig.badge}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${statusConfig.dot}`} />
                    {statusConfig.label}
                  </span>

                  {app.videoUrl && (
                    <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm">
                      <PlayCircleIcon className="h-4 w-4" />
                    </span>
                  )}

                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent px-4 pb-3 pt-10">
                    <h3 className="truncate text-lg font-semibold text-white">
                      {app.name || "Applicant"}
                    </h3>
                    <div className="mt-0.5 flex items-center gap-1 text-sm text-white/85">
                      <MapPinIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{app.city || "Unknown city"}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {timeAgo(app.createdAt)}
                  </div>
                  {app.answers?.whyHost ? (
                    <p className="line-clamp-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                      {app.answers.whyHost}
                    </p>
                  ) : (
                    <p className="text-sm italic text-gray-400 dark:text-gray-500">
                      No pitch provided.
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {viewing && (
        <ApplicationDetailModal
          application={viewing}
          photoURL={viewing.photoURL || photoByUid[viewing.uid]}
          onClose={() => setViewing(null)}
          onReview={review}
          onDelete={remove}
          isUpdating={updatingId === viewing.id}
          isDeleting={deletingId === viewing.id}
          canReview={globalState.isSuperAdmin}
        />
      )}
    </div>
  );
}

function ApplicationDetailModal({ application, photoURL, onClose, onReview, onDelete, isUpdating, isDeleting, canReview }) {
  const [reviewNotes, setReviewNotes] = useState(application.reviewNotes || "");
  const statusConfig = STATUS_CONFIG[application.status] || STATUS_CONFIG.pending;
  const isOpen = application.status === "pending" || application.status === "under-review";
  const busy = isUpdating || isDeleting;

  return (
    <Dialog open={true} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm transition-opacity" />
      <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-start sm:p-4 lg:p-8">
        <DialogPanel className="relative flex w-full max-h-[92vh] flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-gray-800 sm:max-w-2xl sm:rounded-2xl sm:max-h-[calc(100vh-2rem)]">
          <button
            onClick={onClose}
            className="absolute right-3 top-3 z-10 rounded-full bg-black/40 p-1.5 text-white ring-1 ring-white/20 backdrop-blur-sm transition-colors hover:bg-black/60"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="flex-1 overflow-y-auto">
            {application.videoUrl && (
              <div className="flex justify-center overflow-hidden bg-black sm:rounded-t-2xl">
                <video
                  src={application.videoUrl}
                  controls
                  autoPlay
                  playsInline
                  className="max-h-[58vh] w-auto max-w-full"
                />
              </div>
            )}

            <div className="space-y-5 p-5 sm:p-6">
              {/* Applicant header */}
              <div className="flex items-start gap-4">
                {photoURL ? (
                  <img
                    src={variantURL(photoURL, 200)}
                    onError={fallbackToOriginal(photoURL)}
                    alt={application.name || "Applicant"}
                    className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-1 ring-gray-200 dark:ring-white/10"
                  />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-700">
                    <UserCircleIcon className="h-10 w-10 text-gray-300 dark:text-gray-500" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <DialogTitle as="h2" className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {application.name || "Applicant"}
                    </DialogTitle>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusConfig.badge}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${statusConfig.dot}`} />
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      <MapPinIcon className="h-4 w-4" />
                      {application.city || "N/A"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      {formatDate(application.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact / meta tiles */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-900/50">
                  <dt className="text-xs font-medium text-gray-400 dark:text-gray-500">Phone</dt>
                  <dd className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">
                    {application.phone || "—"}
                  </dd>
                </div>
                {application.email && (
                  <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-900/50">
                    <dt className="text-xs font-medium text-gray-400 dark:text-gray-500">Email</dt>
                    <dd className="mt-0.5 break-all text-sm text-gray-800 dark:text-gray-200">
                      {application.email}
                    </dd>
                  </div>
                )}
                {application.reviewedAt && (
                  <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-900/50">
                    <dt className="text-xs font-medium text-gray-400 dark:text-gray-500">Reviewed</dt>
                    <dd className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">
                      {formatDate(application.reviewedAt)}
                    </dd>
                  </div>
                )}
              </div>

              {/* Answers */}
              {application.answers && Object.keys(ANSWER_LABELS).some((k) => application.answers[k]) && (
                <div className="space-y-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                  {Object.keys(ANSWER_LABELS)
                    .filter((k) => application.answers[k])
                    .map((k) => (
                      <div key={k}>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                          {ANSWER_LABELS[k]}
                        </p>
                        {k === "socialLink" ? (
                          <a
                            href={
                              /^https?:\/\//.test(application.answers[k])
                                ? application.answers[k]
                                : `https://${application.answers[k]}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 break-all text-sm font-medium text-primary hover:underline"
                          >
                            <LinkIcon className="h-4 w-4" />
                            {application.answers[k]}
                          </a>
                        ) : (
                          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                            {application.answers[k]}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {application.status === "rejected" && application.reviewNotes && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-500/20 dark:bg-rose-500/10">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-rose-500 dark:text-rose-300">
                    Review notes
                  </p>
                  <p className="text-sm italic text-gray-600 dark:text-gray-300">
                    {application.reviewNotes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {canReview && (
            <div className="flex-shrink-0 space-y-3 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              {isOpen && (
                <>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={2}
                    placeholder="Review notes (optional, saved on the decision)"
                    className="block w-full rounded-lg border-gray-300 p-2.5 text-sm shadow-sm focus:border-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500"
                  />
                  <div className="flex gap-3">
                    <div className="flex flex-1">
                      <button
                        type="button"
                        onClick={() => onReview(application, "rejected", reviewNotes)}
                        disabled={busy}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-l-lg border border-rose-300 bg-white px-3 py-2.5 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-500/30 dark:bg-gray-800 dark:text-rose-300 dark:hover:bg-rose-500/10"
                      >
                        <XCircleIcon className="h-5 w-5" />
                        Reject
                      </button>
                      <Menu as="div" className="relative">
                        <MenuButton
                          disabled={busy}
                          aria-label="More actions"
                          className="inline-flex h-full items-center justify-center rounded-r-lg border border-l-0 border-rose-300 bg-white px-2 text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-500/30 dark:bg-gray-800 dark:text-rose-300 dark:hover:bg-rose-500/10"
                        >
                          <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                        </MenuButton>
                        <MenuItems className="absolute bottom-full left-0 z-20 mb-2 w-48 origin-bottom-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-white/10">
                          <MenuItem>
                            {({ focus }) => (
                              <button
                                type="button"
                                onClick={() => onDelete(application)}
                                disabled={busy}
                                className={`${focus ? "bg-rose-50 dark:bg-rose-500/10" : ""} flex w-full items-center gap-1.5 px-4 py-2 text-left text-sm font-medium text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-rose-300`}
                              >
                                <TrashIcon className="h-4 w-4" />
                                {isDeleting ? "Deleting…" : "Delete application"}
                              </button>
                            )}
                          </MenuItem>
                        </MenuItems>
                      </Menu>
                    </div>
                    <button
                      type="button"
                      onClick={() => onReview(application, "approved", reviewNotes)}
                      disabled={busy}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                      {isUpdating ? "Working…" : "Approve & provision host"}
                    </button>
                  </div>
                </>
              )}
              {!isOpen && (
                <>
                  {application.status === "rejected" && (
                    <button
                      type="button"
                      onClick={() => onReview(application, "reopen", "")}
                      disabled={busy}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      <ArrowUturnLeftIcon className="h-5 w-5" />
                      {isUpdating ? "Reopening…" : "Reopen application"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(application)}
                    disabled={busy}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {isDeleting ? "Deleting…" : "Delete application"}
                  </button>
                </>
              )}
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
